import React, {useContext, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faCheckDouble,
  faClone,
  faClose,
  faPlus,
  faTag,
  faUndo,
  faSitemap,
  faTrash
} from "@fortawesome/free-solid-svg-icons";
import {DragDropContext, Droppable} from "react-beautiful-dnd";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import styled from "styled-components";

// Utils
import {addField, deepCopyBuilder, FIELD, removeElement} from "../../utils/builder";
import {
  applyBulkCondition,
  cloneFieldToDestination,
  getBulkCreateDestinationLabelError,
  getBulkCreateDestinations,
  getLinkedComponentOptions,
  getLinkedGroupOptions
} from "./helpers";

// Contexts
import {FacilityNavContext} from "../../contexts/facilitynav";
import {AuthContext} from "../../contexts/auth";

// Hooks
import {useToast} from "../../contexts/toast";
import useApi from "../../hooks/useApi";

// Components
import ModalRedirect from "../general/ModalRedirect";
import ModalConditionals from "./ModalConditionals";
import Modal from "../../components/Modal";
import ModalDeleteElement from "./ModalDeleteElement";
import ModalAddField from "../general/ModalAddField";
import RenderChecksheetBuilder from "./RenderChecksheetBuilder";
import InputSelect from "../../components/form/InputSelect";
import SearchSelect from "../../components/SearchSelect";
import MultiSelect from "../../components/MultiSelect";

// Style
import {voice} from "../../style/components/typography";
import {pad} from "../../style/components/variables";
import {
  Abbr,
  Button,
  Error,
  Form,
  FormField,
  HeadingCenter,
  Pill,
  Text
} from "../../style/components/general";

const recurseCopy = (ids, currentById, byId, allIds, mode, level = 0) => {
  let hasFields = false;
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    const item = currentById[id];
    if (item) {
      if (item.element === FIELD && !item.stale && !item.deleted && item.toggle) {
        byId[id] = {...item, toggle: mode === "delete" ? true : item.element !== FIELD};
        if (level === 0) allIds.push(id);
        hasFields = true;
      } else if (item.children?.length && item.toggle) {
        const result = recurseCopy(item.children, currentById, byId, allIds, mode, level + 1);
        if (result) {
          hasFields = true;
          byId[id] = {
            ...item,
            children: [...item.children],
            toggle: mode === "delete" ? true : item.element !== FIELD
          };
          if (level === 0) allIds.push(id);
        }
      }
    }
  }
  return hasFields;
};

const prepareBuilder = (builder, mode) => {
  const newById = {};
  const newAllIds = [];
  recurseCopy(builder.allIds, builder.byId, newById, newAllIds, mode);
  return {byId: newById, allIds: newAllIds};
};

const resetBulkState = setters => {
  const {
    setMode,
    setConfirmedFields,
    setSelectedBuilder,
    setSelectionError,
    setSelectedTag,
    setFieldToCreate,
    setSelectedDestinations,
    setDestinationError,
    setHasSubmittedBulkCreate
  } = setters;
  setMode(undefined);
  setConfirmedFields(undefined);
  setSelectedBuilder(undefined);
  setSelectionError(undefined);
  setSelectedTag(undefined);
  setFieldToCreate(undefined);
  setSelectedDestinations([]);
  setDestinationError(undefined);
  setHasSubmittedBulkCreate(false);
};

const ModalBulk = ({
  visible = false,
  setVisible,
  save,
  builder,
  notifications,
  hasPrimaryAddress,
  fieldList,
  units = []
}) => {
  const {facility} = useContext(FacilityNavContext);
  const {atLeast} = useContext(AuthContext);
  const {addToast} = useToast();

  const {api: apiFieldTags} = useApi("field-tags", {suppress: {success: true, error: true}});

  const [mode, setMode] = useState();
  const [confirmedFields, setConfirmedFields] = useState();
  const [selectedBuilder, setSelectedBuilder] = useState();
  const [selectionError, setSelectionError] = useState();
  const [selectedTag, setSelectedTag] = useState();
  const [fieldTags, setFieldTags] = useState();
  const [tagResults, setTagResults] = useState();
  const [fieldToCreate, setFieldToCreate] = useState();
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [destinationError, setDestinationError] = useState();
  const [hasSubmittedBulkCreate, setHasSubmittedBulkCreate] = useState(false);

  const schema = yup.object().shape({
    linkedGroup: yup.string().nullable(),
    linkedComponent: yup.string().nullable()
  });

  const form = useForm({resolver: yupResolver(schema)});

  const {watch, handleSubmit, reset} = form;

  const watchLinkedGroup = watch("linkedGroup");

  const destinationFieldErrors = useMemo(() => {
    if (!fieldToCreate?.label) return {};

    const errors = {};
    selectedDestinations.forEach(destination => {
      const error = getBulkCreateDestinationLabelError(builder, fieldToCreate.label, destination);
      if (error) errors[destination] = error;
    });

    return errors;
  }, [builder, fieldToCreate, selectedDestinations]);

  const stateSetters = {
    setMode,
    setConfirmedFields,
    setSelectedBuilder,
    setSelectionError,
    setSelectedTag,
    setFieldToCreate,
    setSelectedDestinations,
    setDestinationError,
    setHasSubmittedBulkCreate
  };

  useEffect(() => {
    if (!visible) resetBulkState(stateSetters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (mode && mode !== "create") setSelectedBuilder(prepareBuilder(builder, mode));
  }, [mode, builder]);

  useEffect(() => {
    if (mode === "tag" && !fieldTags)
      apiFieldTags.callGet().then(({status, data}) => {
        if (status === 200 && data) {
          setFieldTags(data);
          setTagResults(data);
        }
      });
  }, [mode, fieldTags, apiFieldTags]);

  const handleDeleteFields = () => {
    let updated = deepCopyBuilder(builder);

    confirmedFields.map(field => {
      updated = removeElement(
        updated,
        builder.byId[field],
        !!fieldList &&
          (fieldList[field] ||
            updated.byId[field].children?.some(
              child =>
                fieldList[child] || updated.byId[child]?.children?.some(child2 => fieldList[child2])
            ) ||
            false)
      );
    });

    save(updated);
    setVisible(false);
  };

  const handleSaveConditionals = ({condition}) => {
    save(applyBulkCondition(deepCopyBuilder(builder), confirmedFields, condition));
    setVisible(false);
  };

  const handleSaveTags = () => {
    const updated = deepCopyBuilder(builder);
    confirmedFields.forEach(field => {
      updated.byId[field] = {...updated.byId[field], tag: selectedTag};
    });
    save(updated);
    setVisible(false);
  };

  const handleMoveFields = ({linkedGroup, linkedComponent}) => {
    const updated = deepCopyBuilder(builder);
    const newParentName = linkedComponent || linkedGroup || "";
    confirmedFields.map(field => {
      const {parentName} = updated.byId[field];
      if (parentName !== newParentName) {
        if (parentName)
          updated.byId[parentName].children = updated.byId[parentName].children.filter(
            child => child !== field
          );
        else updated.allIds = updated.allIds.filter(id => id !== field);

        if (newParentName) {
          updated.byId[newParentName].children.push(field);
          updated.byId[field].parentName = newParentName;
        } else {
          updated.allIds.push(field);
        }
      }
    });
    save(updated);
    setVisible(false);
  };

  const handleCloneFields = ({linkedGroup, linkedComponent}) => {
    const destination = linkedComponent || linkedGroup || "";
    let updated = deepCopyBuilder(builder);
    let successCount = 0;

    confirmedFields.forEach(fieldName => {
      const result = cloneFieldToDestination(updated, fieldName, destination);
      if (result.error) addToast(result.error, "error");
      else {
        updated = result.builder;
        successCount++;
      }
    });

    if (successCount > 0) {
      save(updated);
      setVisible(false);
    }
  };

  const handleBulkCreateFields = () => {
    setHasSubmittedBulkCreate(true);
    if (!selectedDestinations.length) {
      setDestinationError("You must select at least one destination.");
      return;
    }

    setDestinationError(undefined);

    let updated = deepCopyBuilder(builder);
    const newUnits = [];
    let successCount = 0;

    if (selectedDestinations.some(destination => destinationFieldErrors[destination])) return;

    selectedDestinations.forEach(destination => {
      if (fieldToCreate.units && !units.includes(fieldToCreate.units)) {
        if (!newUnits.includes(fieldToCreate.units)) newUnits.push(fieldToCreate.units);
      }

      updated = addField(updated, {...fieldToCreate, parentName: destination, toggle: true});
      successCount++;
    });

    if (successCount > 0) {
      save(updated, newUnits.length > 0 ? newUnits : undefined);
      setVisible(false);
    }
  };

  const handleDestinationSelection = selection => {
    setSelectedDestinations(selection);
    setDestinationError(undefined);
  };

  const processSelection = () => {
    const confirmed = Object.entries(selectedBuilder.byId)
      .filter(([, {element, toggle, type}]) => {
        if ((!toggle && mode !== "delete") || (toggle && mode === "delete")) return false;
        if (mode === "delete") return true;
        if (mode === "clone") return element === FIELD && type !== "generated";
        return element === FIELD;
      })
      .map(([id]) => id);

    if (confirmed.length === 0)
      setSelectionError(`You must select at least one ${mode === "delete" ? "element" : "field"}.`);
    else {
      setConfirmedFields(confirmed);
      setSelectionError(undefined);
    }
  };

  const componentInSetGroup = (parentName, currentGroup) => {
    if (!currentGroup) currentGroup = watchLinkedGroup;
    if (!currentGroup || currentGroup === "") return true;
    return currentGroup === parentName;
  };

  const renderFieldOptions = onSubmit => {
    const linkedGroups = getLinkedGroupOptions(builder);
    const linkedComponents = getLinkedComponentOptions(builder, watchLinkedGroup || "", {
      predicate: ({parentName}) => componentInSetGroup(parentName, watchLinkedGroup)
    });
    return (
      <FormProvider {...form}>
        <Form onSubmit={handleSubmit(onSubmit)} noValidate>
          {linkedGroups.length > 0 && (
            <FormField>
              <InputSelect
                name="linkedGroup"
                options={linkedGroups}
                placeholder="Link to Group..."
                label="Linked Group"
              />
            </FormField>
          )}
          {watchLinkedGroup && linkedComponents.length > 0 && (
            <FormField>
              <InputSelect
                name="linkedComponent"
                options={linkedComponents}
                placeholder="Link to Component..."
                label="Linked Component"
              />
            </FormField>
          )}
          <Button type="submit">Save</Button>
        </Form>
      </FormProvider>
    );
  };

  if (mode === "create" && !fieldToCreate)
    return (
      <ModalAddField
        visible={visible}
        setVisible={setVisible}
        hasBackButton
        goBack={() => setMode(undefined)}
        single
        skipLabelUniqueness
        stayOpen
        builder={builder}
        units={units}
        addFields={fields => setFieldToCreate(fields[0])}
      />
    );

  if (mode === "create" && fieldToCreate)
    return (
      <Modal
        visible={visible}
        setVisible={setVisible}
        goBack={() => {
          setFieldToCreate(undefined);
          setSelectedDestinations([]);
          setDestinationError(undefined);
          setHasSubmittedBulkCreate(false);
        }}
        hasBackButton>
        <HeadingCenter>Select Destinations</HeadingCenter>
        <Text small>Select one or more destinations to create this field in.</Text>
        <br />
        <MultiSelect
          testId="bulk.create.destinations"
          label="Destinations"
          options={getBulkCreateDestinations(builder)}
          setSelection={handleDestinationSelection}
          optionErrors={hasSubmittedBulkCreate ? destinationFieldErrors : undefined}
        />
        <br />
        {destinationError && <Error>{destinationError}</Error>}
        <br />
        <Button type="button" onClick={handleBulkCreateFields}>
          Save
        </Button>
      </Modal>
    );

  if (mode && mode !== "create" && !confirmedFields && selectedBuilder)
    return (
      <Modal
        visible={visible && !!mode && !confirmedFields && !!selectedBuilder}
        setVisible={setVisible}
        goBack={() => {
          setMode(undefined);
          setConfirmedFields(undefined);
          setSelectedBuilder(undefined);
          setSelectionError(undefined);
        }}
        hasBackButton>
        <HeadingCenter>Select Elements</HeadingCenter>

        <Text small>
          {mode === "delete" ? "Toggle on any elements you would like to delete." : ""}
          {mode === "move" ? "Toggle on any fields you would like to move." : ""}
          {mode === "clone" ? "Toggle on any fields you would like to clone." : ""}
          {mode === "condition"
            ? "Toggle on any fields you would like to apply a condition to."
            : ""}
          {mode === "tag" ? "Toggle on any fields you would like to tag." : ""}
        </Text>
        <br />

        <DragDropContext onDragStart={() => {}} onDragEnd={() => {}}>
          <Droppable droppableId="checksheet.builder" isDropDisabled>
            {provided => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                <RenderChecksheetBuilder
                  builder={selectedBuilder}
                  setBuilder={setSelectedBuilder}
                  toggleOnly
                  fieldToggleOnly={mode !== "delete"}
                  propagateToggle={false}
                  allowToggleChild={mode !== "delete"}
                  preventToggleRegulatory={mode === "delete" && !atLeast("super")}
                  toggleOnIcon={mode === "delete" ? faUndo : undefined}
                  toggleOffIcon={mode === "delete" ? faTrash : undefined}
                  toggleOnLabel={mode === "delete" ? "Undo" : undefined}
                  toggleOffLabel={mode === "delete" ? "Delete" : undefined}
                  inBulk
                  toggleDisabledAction={(target, fireToast = true) => {
                    if (mode !== "condition") return undefined;
                    if (!facility?.builder?.byId?.[target.name]?.condition?.list?.length)
                      return undefined;
                    if (fireToast)
                      addToast("Must edit facility-wide conditions in Facility Builder");
                    return true;
                  }}>
                  {provided.placeholder}
                </RenderChecksheetBuilder>
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <br />
        {selectionError && <Error>{selectionError}</Error>}
        <br />
        <Button type="button" onClick={() => processSelection()}>
          Continue
        </Button>
      </Modal>
    );

  if (mode === "delete" && confirmedFields)
    return (
      <ModalDeleteElement
        fields={confirmedFields}
        visible={visible && mode === "delete" && !!confirmedFields}
        setVisible={setVisible}
        hasBackButton
        goBack={() => setConfirmedFields(undefined)}
        persistDelete={handleDeleteFields}
        builder={builder}
        notifications={notifications}
      />
    );

  if (mode === "condition" && confirmedFields)
    return (
      <ModalConditionals
        fields={confirmedFields}
        visible={visible && mode === "condition" && !!confirmedFields}
        setVisible={setVisible}
        hasBackButton
        goBack={() => setConfirmedFields(undefined)}
        save={handleSaveConditionals}
        builder={builder}
        hasPrimaryAddress={hasPrimaryAddress}
        bulk
      />
    );

  if (mode === "tag" && confirmedFields)
    return (
      <Modal
        visible={visible && mode === "tag" && !!confirmedFields}
        setVisible={setVisible}
        goBack={() => {
          setConfirmedFields(undefined);
          setSelectedTag(undefined);
        }}
        hasBackButton>
        <HeadingCenter>Tag Fields</HeadingCenter>
        <Text small>Select or create a tag to apply to the selected field(s).</Text>
        <br />
        <FormField>
          {!selectedTag ? (
            <SearchWrapper>
              <SearchSelect
                testId="bulk.tags"
                label="Tag"
                placeholder="Search tags..."
                results={tagResults}
                setResults={setTagResults}
                search={query => {
                  if (!query) setTagResults(fieldTags);
                  else
                    setTagResults(
                      fieldTags?.filter(({name: tagName}) =>
                        tagName.toLowerCase().includes(query.toLowerCase())
                      )
                    );
                }}
                add={selected => {
                  const tagName = typeof selected === "string" ? selected : selected.name;
                  if (!fieldTags?.some(({name: existing}) => existing === tagName)) {
                    setFieldTags(prev => (prev ? [...prev, {name: tagName}] : [{name: tagName}]));
                    apiFieldTags.callPost({name: tagName});
                  }
                  setSelectedTag(tagName);
                }}
                showAll
                allowNew
              />
            </SearchWrapper>
          ) : (
            <SelectedContainer data-testid="bulk.tag-selected">
              <Abbr>{selectedTag}</Abbr>
              &nbsp;
              <IconButton type="button" onClick={() => setSelectedTag(undefined)}>
                <FontAwesomeIcon icon={faClose} />
              </IconButton>
            </SelectedContainer>
          )}
        </FormField>
        <br />
        <Button type="button" disabled={!selectedTag} onClick={handleSaveTags}>
          Save
        </Button>
      </Modal>
    );

  if (mode === "move" && confirmedFields)
    return (
      <Modal
        visible={visible && mode === "move" && !!confirmedFields}
        setVisible={setVisible}
        goBack={() => {
          setConfirmedFields(undefined);
          reset();
        }}
        hasBackButton>
        <HeadingCenter>Move Fields</HeadingCenter>
        {renderFieldOptions(handleMoveFields)}
      </Modal>
    );

  if (mode === "clone" && confirmedFields)
    return (
      <Modal
        visible={visible && mode === "clone" && !!confirmedFields}
        setVisible={setVisible}
        goBack={() => {
          setConfirmedFields(undefined);
          reset();
        }}
        hasBackButton>
        <HeadingCenter>Clone Fields</HeadingCenter>
        <Text small>Select a destination for the cloned fields.</Text>
        <br />
        {renderFieldOptions(handleCloneFields)}
      </Modal>
    );

  return (
    <ModalRedirect
      title="Bulk Operations"
      visible={visible && (!mode || (mode === "create" ? !fieldToCreate : !selectedBuilder))}
      setVisible={setVisible}
      buttonOptions={[
        {action: () => setMode("condition"), description: "Conditional", icon: faCheckDouble},
        {action: () => setMode("delete"), description: "Delete", icon: faTrash},
        {action: () => setMode("move"), description: "Move", icon: faSitemap},
        {action: () => setMode("tag"), description: "Tag", icon: faTag},
        {action: () => setMode("clone"), description: "Bulk Clone", icon: faClone},
        {action: () => setMode("create"), description: "Bulk Field Create", icon: faPlus}
      ]}
      shouldHide={false}
    />
  );
};

const SearchWrapper = styled.div`
  max-width: 200px;
`;

const SelectedContainer = styled(Pill)`
  width: min-content;
  height: min-content;
  max-width: 100%;
  margin: ${pad}px ${pad}px 0 0;
  color: ${({theme}) => theme.tertiary};
`;

const IconButton = styled(Button)`
  ${voice.quiet};
  background-color: transparent;
  width: min-content;
  padding: 0;

  svg {
    fill: ${({theme}) => theme.tertiary};
  }
`;

ModalBulk.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired,
  builder: PropTypes.objectOf(PropTypes.any).isRequired,
  notifications: PropTypes.objectOf(PropTypes.any),
  hasPrimaryAddress: PropTypes.bool,
  fieldList: PropTypes.objectOf(PropTypes.any),
  units: PropTypes.arrayOf(PropTypes.string)
};

export default ModalBulk;
